import { useRouter } from "next/router";
import React from "react";
import { cs } from "../../core/classSet";
import { useBooleanState } from "../../core/hooks/useBooleanState";
import useClickAway from "../../core/hooks/useClickAway";
import { useDidMount } from "../../core/hooks/useDidUpdate";
import { useTabletLargeMediaQuery } from "../../core/hooks/useMediaQuery";
import { getContactSalesUrl, getLandingPageUrl, getMaintainXUrl } from "../../core/maintainxUrl";
import { IIndustry } from "../../typings/filterEntities";
import Button from "../Button/Button";
import { ArrowIcon2, MenuIcon, StarsIcon, industryIconMap } from "../Icons/Icons";
import Logo from "../Logo/Logo";
import NavDrawer from "../NavDrawer/NavDrawer";
import NavBarSubMenu from "../NavbarSubMenu/NavbarSubMenu";
import styles from "./Navbar.module.css";

export const NAVBAR_SUB_MENUS = {
  industries: {
    id: "industries",
    label: "Our Industries",
    items: [
      {
        internalHref: "/industries/manufacturing/",
        label: "Manufacturing",
        slug: "manufacturing",
        text: "Improve OEE, improve safety, and reduce downtime.",
      },
      {
        internalHref: "/industries/facility-management/",
        label: "Facility Management",
        slug: "property-management",
        text: "Increase asset lifespan and manage standard operating procedures.",
      },
      {
        internalHref: "/industries/food-and-beverage/",
        label: "Food and Beverage",
        slug: "food-beverage",
        text: "Keep your assets running and manage checklists across all of your locations.",
      },
      {
        internalHref: "/industries/hospitality/",
        label: "Hospitality",
        slug: "hospitality",
        text: "Make sure your facilities impress your customers.",
      },
      {
        internalHref: "/industries/education-and-schools/",
        label: "Education and Schools",
        slug: "education",
        text: "Ensure your facilities enable teachers and students to do their best.",
      },
      {
        internalHref: "/industries/clubs-and-associations/",
        label: "Clubs and Associations",
        slug: "general",
        text: "Keep your equipment and facilities operational for your customers.",
      },
      {
        internalHref: "/industries/property-management/",
        label: "Property Management",
        slug: "property-management",
        text: "Track work requests and keep your tenants happy.",
      },
    ],
  },

  useCases: {
    id: "useCases",
    label: "Our Use Cases",
    items: [
      {
        internalHref: "/use-cases/equipment-and-asset-management/",
        label: "Asset Management",
        slug: "asset-management",
        text: "Reduce downtime and improve productivity.",
      },
      {
        internalHref: "/use-cases/preventive-maintenance/",
        label: "Preventive Maintenance",
        slug: "preventive",
        text: "Schedule repeatable tasks for your assets with ease.",
      },
      {
        internalHref: "/use-cases/facility-management/",
        label: "Facility Maintenance",
        slug: "facility",
        text: "Keep things running smoothly and receive work requests.",
      },
      {
        internalHref: "/use-cases/checklists-and-inspections/",
        label: "Checklists and Inspections",
        slug: "checklists",
        text: "Manage safety, regulatory compliance, and standard operating procedures on the go.",
      },
      {
        internalHref: "/use-cases/parts-inventory-management/",
        label: "Parts Inventory Management",
        slug: "inventory",
        text: "Track usage by work order and asset. Create low quantity alerts.",
      },
      {
        internalHref: "/use-cases/work-order-management/",
        label: "Work Order Management",
        slug: "workOrder",
        text: "Assign, comment, and track the status in real-time.",
      },
    ],
  },

  resources: {
    id: "resources",
    label: "Resources",
    items: [
      {
        internalHref: "/learning-center/",
        label: "Get Educated",
        slug: "learning-center",
        text: "MaintainX Learning Center",
      },
      {
        externalHref: "https://academy.getmaintainx.com/certifications",
        label: "Get Certified",
        slug: "training-center",
        text: "MaintainX Training Center",
      },
      {
        internalHref: "/blog/",
        label: "Blog",
        slug: "blog",
        text: "Latest Updates & Articles",
      },
      {
        externalHref: "https://maintainx.dev/",
        label: "API Documentation",
        slug: "api",
        text: "Interact with all MX entities",
      },
      {
        internalHref: "/case-studies/",
        label: "Case Studies",
        slug: "case-studies",
        text: "Client experiences with MX",
      },
      {
        externalHref: "https://community.getmaintainx.com/",
        label: "Community",
        slug: "community",
        text: "Connect, Learn, and Ask Questions with Industry Leaders",
      },
    ],
  },
};

export type NavbarSubMenuItem = {
  externalHref?: string;
  internalHref?: string;
  icon?: React.ReactNode;
  label: string;
  slug: string;
  text?: string;
};

export type NavbarSubMenu = {
  id: string;
  label?: string;
  items: NavbarSubMenuItem[];
};
interface INavItemWithSubMenuProps {
  className?: string;
  label: string;
  subMenu: NavbarSubMenu;
}

export function NavItemWithSubMenu({ className, label, subMenu }: INavItemWithSubMenuProps) {
  const [isShowingSubMenu, , hideSubMenu, toggleSubMenu] = useBooleanState();

  const subNavRef = React.useRef<HTMLLIElement>(null);
  useClickAway({
    callback: hideSubMenu,
    ref: subNavRef,
    enable: isShowingSubMenu,
  });

  const router = useRouter();

  useDidMount(() => {
    router.events.on("routeChangeComplete", hideSubMenu);
    return () => router.events.off("routeChangeComplete", hideSubMenu);
  });

  return (
    <li ref={subNavRef} className={styles.navItemFrame}>
      <div className={cs(styles.navItem, className)}>
        <button
          className={cs(styles.navLink, isShowingSubMenu && styles.navLinkActive, className)}
          type="button"
          onClick={toggleSubMenu}
        >
          {label}{" "}
          <span className={styles.arrow}>
            <ArrowIcon2 direction={isShowingSubMenu ? "up" : "down"} />
          </span>
        </button>
      </div>
      {isShowingSubMenu ? <NavBarSubMenu subMenu={subMenu} /> : null}
    </li>
  );
}

interface IProps {
  industries: IIndustry[];
}

export default function Navbar({ industries }: IProps) {
  const [isShowingMobileNav, showMobileNav, hideMobileNav] = useBooleanState(false);

  const subMenuGpl = React.useMemo(() => {
    return {
      id: "gpl-industries",
      label: "Industries",
      items: [
        {
          icon: <StarsIcon width={16} height={16} />,
          label: "All Industries",
          slug: "",
        },
        ...industries.map((i) => {
          const Icon = industryIconMap[i.slug];
          return {
            icon: Icon ? <Icon width={16} height={16} /> : undefined,
            label: i.label,
            slug: i.slug,
          };
        }),
      ],
    };
  }, [industries]);

  const isTabletLargeViewport = useTabletLargeMediaQuery();

  React.useEffect(() => {
    if (isTabletLargeViewport) {
      hideMobileNav();
    }
  }, [isTabletLargeViewport, hideMobileNav]);

  const router = useRouter();

  useDidMount(() => {
    router.events.on("routeChangeComplete", hideMobileNav);
    return () => router.events.off("routeChangeComplete", hideMobileNav);
  });

  return (
    <>
      <NavDrawer show={isShowingMobileNav} subMenuGpl={subMenuGpl} onHide={hideMobileNav} />
      <nav className={styles.root}>
        <button className={styles.mobileNavToggle} type="button" onClick={showMobileNav}>
          <MenuIcon />
        </button>
        <div className={styles.logo}>
          <a href={getLandingPageUrl()}>
            <Logo width={170} height={24} />
          </a>
        </div>
        <ul className={styles.navMenu}>
          <NavItemWithSubMenu label="Industries" subMenu={NAVBAR_SUB_MENUS.industries} />
          <NavItemWithSubMenu label="Use Cases" subMenu={NAVBAR_SUB_MENUS.useCases} />
          <li className={cs(styles.navItem, styles.desktopOnly)}>
            <a className={styles.navLink} href={getLandingPageUrl("/pricing/")}>
              Pricing
            </a>
          </li>
          <NavItemWithSubMenu
            className={styles.desktopOnly}
            label="Resources"
            subMenu={NAVBAR_SUB_MENUS.resources}
          />
          <li className={cs(styles.navItem, styles.navItemButton)}>
            <a href={getContactSalesUrl()}>
              <Button btnStyle="outline" iconRight={<ArrowIcon2 />} bigLineHeight={true}>
                <span className={styles.navButtonOutline}>Book a Tour</span>
              </Button>
            </a>
          </li>
          <li className={cs(styles.navItem, styles.navItemButton)}>
            <a href={getMaintainXUrl()}>
              <Button btnStyle="solid" iconRight={<ArrowIcon2 />} bigLineHeight={true}>
                Log In to MaintainX
              </Button>
            </a>
          </li>
        </ul>
      </nav>
    </>
  );
}
